import React, { useState, useRef, useEffect } from 'react';
import { Building2 } from 'lucide-react';
import AddExperienceModal from '../components/AddExperienceModal';
import ConfirmDeleteModal from '../../../organisms/ConfirmDeleteModal';
import Toast from '../../../atoms/Toast';
import { API_ENDPOINTS } from '../../../../config';
import { api } from '../../../../utils/api';
import '../../../../styles/profile/experience-tab.css';

interface ExperienceCardProps {
  experience: WorkExperience;
  onEdit: (experience: WorkExperience) => void;
  onDelete: (uid: string) => void;
}

function ExperienceCard({ experience, onEdit, onDelete }: ExperienceCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const formatDateRange = (): string => {
    const startDate = formatDate(experience.start_date);
    const endDate = experience.is_current ? 'Present' : formatDate(experience.end_date);

    if (startDate && !endDate) {
      return `from ${startDate}`;
    }
    if (!startDate && endDate) {
      return `upto ${endDate}`;
    }
    if (startDate && endDate) {
      return `${startDate} - ${endDate}`;
    }
    return '';
  };

  const dateRange = formatDateRange();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  return (
    <div className="experience-card">
      <div className="experience-card-header">
        <div className="experience-card-logo">
            <span className="experience-card-logo-icon">
              <Building2 size={16} />
            </span>
        </div>
        <div className="experience-card-info">
          <h3 className="experience-card-title">{experience.employer_name}</h3>
          <p className="experience-card-subtitle">{experience.job_title}</p>
        </div>
        {experience.location && (
          <p className="experience-card-location">{experience.location}</p>
        )}
        <p className="experience-card-date">{dateRange}</p>
        <div className="experience-card-menu-container" ref={menuRef}>
          <button 
            className="experience-card-menu" 
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            ⋮
          </button>
          {menuOpen && (
            <div className="experience-card-dropdown">
              <button
                className="experience-card-dropdown-item"
                onClick={() => {
                  onEdit(experience);
                  setMenuOpen(false);
                }}
              >
                Edit experience
              </button>
              <button
                className="experience-card-dropdown-item experience-card-dropdown-item--delete"
                onClick={() => {
                  onDelete(experience.uid);
                  setMenuOpen(false);
                }}
              >
                Delete experience
              </button>
            </div>
          )}
        </div>
      </div>

      {(experience.description || (experience.achievements && experience.achievements.length > 0)) && (
        <div className="experience-card-body">
          {experience.description && (
            <p className="experience-card-description">{experience.description}</p>
          )}
          {experience.achievements && experience.achievements.length > 0 && (
            <ul className="experience-card-achievements">
              {experience.achievements.map((achievement, index) => (
                <li key={index}>{achievement}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {experience.skills && experience.skills.length > 0 && (
        <div className="experience-card-skills">
          <span className="experience-card-skills-label">Skills:</span>
          <div className="experience-card-skills-list">
            {experience.skills.map((skill, index) => (
              <span key={index} className="experience-card-skill-chip">
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface ExperienceData {
  uid?: string;
  employer: string;
  jobTitle: string;
  space: string;
  role: string;
  startDate: string;
  endDate: string;
  location: string;
  isCurrentPosition: boolean;
  skills: string[];
  description: string;
  achievements: string[];
}

interface WorkExperience {
  uid: string;
  employer_name: string;
  job_title: string;
  skills: string[] | null;
  description: string | null;
  achievements: string[] | null;
  start_date: string;
  end_date: string | null;
  location: string | null;
  is_current: boolean;
  created_at: string;
  updated_at: string;
}

interface UserData {
  experience: WorkExperience[] | null;
}

interface ExperienceTabProps {
  userData: UserData | null;
  experiences: WorkExperience[];
}

export default function ExperienceTab({ userData, experiences }: ExperienceTabProps) {
  const [totalExperience, setTotalExperience] = useState('Years of Experience');
  const [experienceDropdownOpen, setExperienceDropdownOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [workExperiences, setWorkExperiences] = useState<WorkExperience[]>([]);
  const [selectedExperience, setSelectedExperience] = useState<WorkExperience | null>(null);
  const [deleteUid, setDeleteUid] = useState<string | null>(null);
  const experienceDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (experiences && experiences.length > 0) {
      setWorkExperiences(experiences);
    }
  }, [experiences]);

  const experienceOptions = [
    '< 1 Year',
    ...Array.from({ length: 20 }, (_, i) => `${i + 1} Year${i + 1 > 1 ? 's' : ''}`),
    '> 20 Years'
  ];

  const handleImport = () => {
  };

  const handleAdd = () => {
    setSelectedExperience(null);
    setAddModalOpen(true);
  };

  const handleEdit = (experience: WorkExperience) => {
    setSelectedExperience(experience);
    setEditModalOpen(true);
  };

  const handleDeleteClick = (uid: string) => {
    setDeleteUid(uid);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteUid) return;

    setIsDeleting(true);

    try {
      
      await api.delete(API_ENDPOINTS.users.deleteExperience(deleteUid));
      
      setWorkExperiences(workExperiences.filter(exp => exp.uid !== deleteUid));
      setToast({ message: 'Work experience deleted successfully! ✓', type: 'success' });
      setDeleteModalOpen(false);
      setDeleteUid(null);
    } catch (error) {
      setToast({ message: 'Failed to delete work experience. Please try again.', type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddExperience = async (data: ExperienceData) => {
    setIsCreating(true);

    try {
      const payload: any = {
        employer_name: data.employer,
        job_title: data.jobTitle,
        is_current: data.isCurrentPosition,
      };

      if (data.uid) {
        payload.uid = data.uid;
      }

      if (data.skills.length > 0) payload.skills = data.skills;
      if (data.description.trim()) payload.description = data.description.trim();
      if (data.achievements.length > 0) payload.achievements = data.achievements;
      if (data.startDate) payload.start_date = data.startDate;
      if (data.endDate) payload.end_date = data.endDate;
      if (data.location.trim()) payload.location = data.location.trim();

      const isEditMode = !!data.uid;

      const response = await api.post(API_ENDPOINTS.users.updateExperience, payload);
      

      if (isEditMode) {
        setWorkExperiences(workExperiences.map(exp => 
          exp.uid === data.uid ? { ...exp, ...response } : exp
        ));
        setToast({ message: 'Work experience updated successfully! ✓', type: 'success' });
        setEditModalOpen(false);
        setSelectedExperience(null);
      } else {
        if (response && response.uid) {
          setWorkExperiences([...workExperiences, response]);
        }
        setToast({ message: 'Work experience added successfully! ✓', type: 'success' });
        setAddModalOpen(false);
      }
    } catch (error) {
      setToast({ message: 'Failed to save work experience. Please try again.', type: 'error' });
    } finally {
      setIsCreating(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (experienceDropdownRef.current && !experienceDropdownRef.current.contains(e.target as Node)) {
        setExperienceDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <div className="experience-tab">
        <div className="experience-tab-header">
          <h1 className="experience-tab-title">Experience</h1>
          <button className="experience-tab-btn experience-tab-btn--import" onClick={handleImport} type="button">
            <span className="experience-tab-btn-icon">⬇</span>
            Import
          </button>
        </div>

        <div className="experience-tab-content">
          <h2 className="experience-tab-subtitle">Total Work Experience</h2>
          <p className="experience-tab-description">
            How many years of total professional experience do you have? (Regardless of role)
          </p>
          
          <div className="experience-tab-dropdown" ref={experienceDropdownRef}>
            <button
              type="button"
              className="experience-tab-dropdown-trigger"
              onClick={() => setExperienceDropdownOpen(!experienceDropdownOpen)}
            >
              <span className="experience-tab-dropdown-value">{totalExperience}</span>
              <span className="experience-tab-dropdown-arrow">▾</span>
            </button>
            {experienceDropdownOpen && (
              <div className="experience-tab-dropdown-menu">
                {experienceOptions.map((option) => (
                  <div
                    key={option}
                    className={`experience-tab-dropdown-item ${totalExperience === option ? 'experience-tab-dropdown-item--selected' : ''}`}
                    onClick={() => {
                      setTotalExperience(option);
                      setExperienceDropdownOpen(false);
                    }}
                  >
                    {option}
                  </div>
                ))}
              </div>
            )}
          </div>

          <button 
            type="button" 
            className="experience-tab-add-btn"
            onClick={handleAdd}
          >
            <span className="experience-tab-add-icon">+</span>
          </button>

          {workExperiences.length > 0 && (
            <div className="experience-list">
              {workExperiences.map((exp) => (
                <ExperienceCard 
                  key={exp.uid} 
                  experience={exp}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <AddExperienceModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={handleAddExperience}
      />

      <AddExperienceModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedExperience(null);
        }}
        onAdd={handleAddExperience}
        title="Edit Work Experience"
        initialData={selectedExperience}
        isEditMode={true}
      />

      <ConfirmDeleteModal
        isOpen={deleteModalOpen}
        itemName={workExperiences.find(exp => exp.uid === deleteUid)?.employer_name || 'this work experience'}
        itemType="Work Experience"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteModalOpen(false);
          setDeleteUid(null);
        }}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}

